<template>
  <div class="dashboard-list-item">
    <div class="item-thumb" :class="{ 'is-loading': imageSrc && !imgLoaded }">
      <img v-if="imageSrc" v-show="imgLoaded" :src="imageSrc" class="thumb-image" alt="Comment thumbnail"
        @load="imgLoaded = true" @error="onImgError" />
      <img v-else class="thumb-image" :src="noImageAvailable" alt="No image available" />
    </div>

    <div class="item-content">
      <div class="item-text">{{ item.comment }}</div>
      <div class="item-meta">
        <span>{{ item.createdBy }}</span>
        <span class="meta-sep">&middot;</span>
        <span>{{ formatDate(item.createdOn) }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useDefaultImage } from '@/utils/ImageUtils';
import { defineProps, ref, watch } from 'vue';
import organizationSasService from '@/services/OrganizationSasService';
import noImageAvailable from '@/assets/no-image-available.svg';

const props = defineProps({
  item: {
    type: Object,
    required: true
  }
});

const imageSrc = ref(null);
const imgLoaded = ref(false);

const onImgError = (e) => {
  useDefaultImage(e);
  imgLoaded.value = true;   // stop shimmering even if the thumbnail failed
};

watch(
  () => props.item,
  async (newVal) => {
    imgLoaded.value = false;
    if (newVal?.thumbnailPath) {
      imageSrc.value = await organizationSasService.getSignedUrl(newVal.thumbnailPath);
    } else {
      imageSrc.value = null;
    }
  },
  { immediate: true }
);

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
</script>

<style scoped>
.dashboard-list-item {
  display: flex;
  align-items: stretch;
  flex: 0 0 auto;   /* keep natural height in the scroll column instead of squishing */
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  overflow: hidden;
  transition: background 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  width: 100%;
}

.dashboard-list-item:hover {
  background: rgba(100, 255, 218, 0.05);
  border-color: rgba(100, 255, 218, 0.15);
}

.item-thumb {
  flex: 0 0 52px;
  width: 52px;
  align-self: stretch;
  min-height: 52px;
  background: #1c272c;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
}

.thumb-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.item-thumb.is-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
  animation: sk-shimmer 1.25s ease-in-out infinite;
}
@keyframes sk-shimmer { 100% { transform: translateX(100%); } }
@media (prefers-reduced-motion: reduce) {
  .item-thumb.is-loading::after { animation: none; }
}

.item-content {
  flex: 1 1 auto;
  min-width: 0;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 3px;
}

.item-text {
  font-size: 0.86rem;
  font-weight: 600;
  color: #ffffff;
  line-height: 1.3;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.item-meta {
  font-size: 0.76rem;
  color: #8aa0aa;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.meta-sep {
  opacity: 0.5;
}
</style>
